import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { GlassPanel } from "./GlassPanel";
import { BrainCircuit, History, Ruler, Loader2 } from "lucide-react";

/**
 * Advanced Quoting settings — surfaces the AI quoting agent's "context"
 * knobs at the top level of Settings (grouped under Advanced · AI tools)
 * so admins don't need to dig into the Quotes-tab modal to flip them.
 *
 * Two toggles:
 *   • Reference past finished jobs → feeds Claude a summary of archived
 *     jobs + per-installation-type averages.
 *   • Area price memory → additionally feeds a table of "wrap_area combos
 *     → invoiced price" so repeat quotes stay consistent.
 *
 * Both persist via PUT /api/quote-agent/context-settings.
 */
export default function AdvancedQuotingSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ctx, setCtx] = useState({
    use_history: true,
    history_count: 15,
    use_area_pricing: false,
    available: 0,
    invoiced_available: 0,
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/quote-agent/context-settings");
        setCtx({
          use_history: !!data.use_history,
          history_count: Number(data.history_count) || 15,
          use_area_pricing: !!data.use_area_pricing,
          available: Number(data.available) || 0,
          invoiced_available: Number(data.invoiced_available) || 0,
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (patch) => {
    const next = { ...ctx, ...patch };
    setCtx(next);
    setSaving(true);
    try {
      await api.put("/quote-agent/context-settings", {
        use_history: next.use_history,
        history_count: next.history_count,
        use_area_pricing: next.use_area_pricing,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassPanel className="!p-6 max-w-3xl" data-testid="settings-advanced-quoting">
      <div className="mb-5">
        <div className="text-label flex items-center gap-2">
          <BrainCircuit className="w-3 h-3" /> Quoting intelligence
        </div>
        <h2 className="font-heading text-2xl text-white mt-1">Advanced Quoting</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-xl">
          Give the Quoting Agent more context from your actual shop history so its numbers stay in line with what you&apos;ve already been charging. Everything here is optional and can be turned back off at any time.
        </p>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-4">
          {/* Toggle 1 — reference past finished jobs */}
          <div
            data-testid="advanced-quoting-history-card"
            className={`rounded-3xl border p-5 transition ${
              ctx.use_history ? "bg-emerald-500/5 border-emerald-400/30" : "bg-white/[0.02] border-white/10"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border ${
                  ctx.use_history ? "bg-emerald-500/15 border-emerald-400/30 text-emerald-200" : "bg-white/5 border-white/10 text-slate-300"
                }`}>
                  <History className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-medium">Reference past finished jobs</div>
                  <p className="text-xs text-slate-400 mt-1 max-w-md">
                    When on, Claude sees a summary of your most recent archived jobs (vehicle, service, amount) and uses them as pricing anchors.
                    {" "}
                    <span className="text-slate-500">
                      {ctx.available > 0
                        ? `Currently ${ctx.available} finished job${ctx.available === 1 ? "" : "s"} in the pool.`
                        : "No finished jobs yet — enable this later once you have a history."}
                    </span>
                  </p>
                </div>
              </div>
              <ToggleSwitch
                testId="advanced-quoting-history-toggle"
                checked={ctx.use_history}
                onChange={(v) => save({ use_history: v })}
                disabled={saving}
              />
            </div>
            {ctx.use_history && (
              <div className="flex items-center gap-3 mt-4 pl-12">
                <label className="text-xs text-slate-400" htmlFor="ax-history-count">Jobs to reference</label>
                <input
                  id="ax-history-count"
                  data-testid="advanced-quoting-history-count"
                  type="number"
                  min={1}
                  max={50}
                  value={ctx.history_count}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(50, Number(e.target.value) || 1));
                    setCtx({ ...ctx, history_count: n });
                  }}
                  onBlur={() => save({ history_count: ctx.history_count })}
                  className="w-20 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white text-center focus:outline-none focus:border-white/30"
                />
                <span className="text-[10px] uppercase tracking-widest text-slate-500">1 – 50 most recent</span>
              </div>
            )}
          </div>

          {/* Toggle 2 — area price memory */}
          <div
            data-testid="advanced-quoting-area-card"
            className={`rounded-3xl border p-5 transition ${
              ctx.use_area_pricing ? "bg-violet-500/5 border-violet-400/30" : "bg-white/[0.02] border-white/10"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border ${
                  ctx.use_area_pricing ? "bg-violet-500/15 border-violet-400/30 text-violet-200" : "bg-white/5 border-white/10 text-slate-300"
                }`}>
                  <Ruler className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-medium">Area price memory</div>
                  <p className="text-xs text-slate-400 mt-1 max-w-md">
                    Additionally feeds Claude a table of the exact area combos you&apos;ve invoiced before (e.g. &ldquo;Fuel tank + Tail fairing&rdquo;) and what you charged. Repeat quotes stay consistent instead of drifting each time.
                    {" "}
                    <span className="text-slate-500">
                      {ctx.invoiced_available > 0
                        ? `Sourced from ${ctx.invoiced_available} invoice${ctx.invoiced_available === 1 ? "" : "s"}.`
                        : "No invoices in the system yet — flip this on once you&rsquo;ve billed a few jobs."}
                    </span>
                  </p>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">
                    Requires &ldquo;Reference past finished jobs&rdquo; to be on.
                  </div>
                </div>
              </div>
              <ToggleSwitch
                testId="advanced-quoting-area-toggle"
                checked={ctx.use_area_pricing}
                onChange={(v) => save({ use_area_pricing: v })}
                disabled={saving || !ctx.use_history}
              />
            </div>
          </div>

          <div className="text-[11px] text-slate-500">
            Changes take effect on the next Auto-quote or Quoting Agent chat.
          </div>
        </div>
      )}
    </GlassPanel>
  );
}

function ToggleSwitch({ testId, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`shrink-0 w-11 h-6 rounded-full border transition-colors relative ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${checked ? "bg-emerald-500/70 border-emerald-300/50" : "bg-white/10 border-white/20"}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
          checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}
